import { GRID_APPLICABILITY_FIELDS } from '../grid-applicability';
import type { QuoteResponseLineInput, QuoteResponseParams, QuoteResponseRequest, QuoteResponseResult } from './quote-response.types';

/**
 * Réponse enregistrée à une demande de tarifs (module `quoteResponse`) : le pendant, côté réponse, d'un appel d'offres.
 * Elle garde la DEMANDE reçue (lignes) et ses RÉGLAGES (indice reçu et son mois, marge, rayons), plus le dernier calcul
 * lancé (`IQuoteReplyComputed`) : la grille remplie n'est recalculée que sur « Lancer » (`POST /quote-replies/:id/launch`),
 * donc elle reste celle du jour où on l'a lancée tant qu'on ne relance pas.
 *
 * Contrat API ↔ Web (additif, isolé).
 */

/** Cycle d'une réponse : en saisie, prête à envoyer, envoyée au client. Changé à la main, jamais automatiquement. */
export type QuoteReplyStatus = 'DRAFT' | 'READY' | 'SENT';

/** Du premier au dernier état ; l'ordre sert aux filtres et au parcours. */
export const QUOTE_REPLY_STATUSES: readonly QuoteReplyStatus[] = ['DRAFT', 'READY', 'SENT'];

/**
 * Réglages d'une réponse. Comme `QuoteResponseParams`, sauf que l'indice et son mois peuvent ne pas être choisis
 * encore (chaîne vide) : une réponse se crée avant d'avoir ouvert la demande reçue. La date de validité des grilles
 * n'y figure pas : c'est celle du jour du calcul.
 */
export type QuoteReplyParams = Omit<QuoteResponseParams, 'indexSourceId' | 'indexMonth' | 'date'> & {
  /** `IndexSource.id`, ou '' tant que l'indice reçu n'est pas choisi. */
  indexSourceId: string;
  /** `YYYY-MM`, ou '' tant que le mois de l'indice reçu n'est pas choisi. */
  indexMonth: string;
};

/** Réglages d'une réponse neuve : la marge et les rayons usuels, aucun indice choisi. */
export const DEFAULT_QUOTE_REPLY_PARAMS: QuoteReplyParams & Required<Pick<QuoteReplyParams, 'rangeMinPrices' | 'groupableCriteria'>> = {
  indexSourceId: '',
  indexMonth: '',
  marginRate: 8,
  marginBasis: 'SALE',
  weighting: 'ALLOCATION',
  originRadiusKm: 50,
  destRadiusKm: 50,
  minResponses: 3,
  rangeMinPrices: 3,
  groupableCriteria: [...GRID_APPLICABILITY_FIELDS],
};

/** Une réponse telle que la liste la rend : sans ses lignes (jusqu'à 500), qu'elle compte seulement. */
export interface IQuoteReplySummary {
  id: string;
  name: string;
  /** Le client qui a envoyé la demande de tarifs. */
  clientName: string | null;
  /** Échéance pour répondre, `YYYY-MM-DD`. */
  dueDate: string | null;
  status: QuoteReplyStatus;
  notes: string | null;
  lineCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Une réponse ouverte : la demande (lignes) et les réglages en plus. */
export interface IQuoteReply extends IQuoteReplySummary {
  lines: QuoteResponseLineInput[];
  params: QuoteReplyParams;
}

/**
 * Le dernier calcul lancé d'une réponse : la demande telle qu'elle partait au calcul (pour savoir si lignes ou réglages
 * ont changé depuis), son résultat, et le moment du lancement.
 */
export interface IQuoteReplyComputed {
  request: QuoteResponseRequest;
  result: QuoteResponseResult;
  /** ISO 8601. */
  computedAt: string;
}

export interface CreateQuoteReplyDto {
  name: string;
  clientName?: string | null;
  dueDate?: string | null;
  notes?: string | null;
}

/** Tout est facultatif : on n'envoie que ce qui change. `lines` et `params` remplacent la valeur entière. */
export interface UpdateQuoteReplyDto {
  name?: string;
  clientName?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  status?: QuoteReplyStatus;
  lines?: QuoteResponseLineInput[];
  params?: QuoteReplyParams;
}
